import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useMeterReaderList, useUpdateMeterReader } from "../../hooks";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { EyeIcon, PenBox, Search } from "lucide-react";
import { Switch } from "@shared/ui/switch";
import { Button } from "@shared/ui/button";
import Edit from "./Edit";
import MeterReaderDetail from "./MeterReaderDetail";
import { logEvent } from "@shared/analytics/analytics";
import { Landing } from "./detail/Landing";
import { PageHeader } from "@shared/components/PageHeader";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Badge } from "@shared/ui/badge";

const MeterReaderManagement = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [details, setDetails] = useState<any>();

  const navigate = useNavigate();
  const currentPage = Number(searchParams.get("page") || "1");
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");

  const { data: meterReaders, isLoading } = useMeterReaderList({
    remoteUtilityId,
    page: currentPage,
    search: searchQuery,
  });

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...({ search: searchTerm }),
    };
    setSearchParams(newParams);
  };

  const { mutate: updateReader } = useUpdateMeterReader();

  const toggleActive = (row: any) => {
    const { id, isActive } = row;
    updateReader({
      id,
      payload: {
        is_active: !isActive,
      },
    }, {
      onSuccess: () => {
        logEvent('MX Meter Reader Toggled', {
          readerId: id,
          is_active: !isActive,
        })
      }
    });
  };

  const columns: TableColumn<any>[] = [
    { key: "employeeId", header: "Emp ID" },
    { key: "name", header: "Name" },
    { key: "email", header: "Email" },
    { key: "contactNumber", header: "Contact" },
    { key: "imeiNo", header: "IMEI No" },
    { key: "createdDate", header: "Created On" },
    { key: "createdUserRemoteName", header: "Created By" },
    {
      key: "isActive",
      header: "Active",
      render: (value, row) => (
        <Switch
          checked={row.isActive}
          onCheckedChange={() => {
            toggleActive(row);
          }}
        />
      ),
    },
    {
      key: "isUserAccess",
      header: "App Account",
      render: (value, row) => (
        <Badge variant={row.isUserAccess ? "default" : "secondary"}>
          {row.isUserAccess ? "Enabled" : "Disabled"}
        </Badge>
      ),
    },
  ];

  const actions = [
    {
      label: "View",
      icon: <EyeIcon className="h-5 w-5 text-muted-foreground" />,
      onClick: (row: any) => {
        const newParams: any = {
          ...Object.fromEntries(searchParams.entries()),
          view: row.id,
        };
        setSearchParams(newParams);
        logEvent("MX Reader Viewed", {
          readerId: row.id,
        });
      },
    },
    {
      label: "Edit",
      icon: <PenBox className="h-5 w-5 text-muted-foreground" />,
      onClick: (row: any) => {
        const newParams: any = {
          ...Object.fromEntries(searchParams.entries()),
          edit: row.id,
        };
        setSearchParams(newParams);
        setDetails(row);
      },
    },
  ];
  const pagination = meterReaders
    ? {
      currentPage,
      totalPages: Math.ceil(meterReaders.count / 10),
      totalItems: meterReaders.count,
      startItem: (currentPage - 1) * 10 + 1,
      endItem: Math.min(currentPage * 10, meterReaders.count),
      itemsPerPage: 10,
    }
    : undefined;

  const viewId = searchParams.get("view");
  const editId = searchParams.get("edit");

  const editDetails = useMemo(() => {
    if (!editId) return null;
    if (details && String(details.id) === editId) return details;
    return meterReaders?.results?.find((r: any) => String(r.id) === editId);
  }, [editId, meterReaders, details]);

  if (viewId) {
    return <Landing />;
  }

  if (editId) {
    return (
      <Edit
        details={editDetails}
        onCancel={() => {
          const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
          delete newParams.edit;
          setSearchParams(newParams);
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Meter Reader Management"
        subtitle="Manage your meter readers"
        buttonName="Add Reader"
        handleSubmit={() => setIsAddModalOpen(true)}
      />
      <GenericTable
        data={meterReaders?.results}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        pagination={pagination}
        actions={actions}
        actionsType="icons"
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        onSearchValueChange={handleSearch}
        searchPlaceholder="Search by name, email, employee-id..."
        searchDebounceMs={500}
        emptyMessage="No meter readers found"
        emptyIcon={<Search className="h-8 w-8 text-muted-foreground mb-2" />}
      />
      <MeterReaderDetail
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        detail={details}
      />

      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Add New Meter Reader</DialogTitle>
            <DialogDescription className="pt-2">
              Would you like to add a new meter reader to your team?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-500">
              Adding a new meter reader will  take you to a form where you can
              enter all the necessary details for onboarding.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddModalOpen(false)}
              className="mt-2 sm:mt-0"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setIsAddModalOpen(false);
                navigate("/utility/utility-setup/user-management");
              }}
              className="bg-blue-600 hover:bg-blue-700">
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeterReaderManagement;
